import { useRef } from 'react';
import { useNavigate } from 'react-router';
import { Timer, Clock, Save, AlertCircle, ExternalLink, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { formatPhoneNumber } from '@intone/shared/utils';
import type { CallWithDetails } from '@intone/shared';
import { useRecordingUrl } from '@/hooks/useRecordingUrl';

import { formatTimestampYear, formatDuration } from '@/shared/lib/date';
import { formatDisconnectionReasonCallCard } from '@/shared/lib/strings';
import { useVoicesList } from '@/entities/voice';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { DispositionBadge } from '@/features/dispositionBadge'
import {
  useCall,
  useTranslator,
  TranscriptDisplay,
} from '@/entities/call';

import { CallQASection } from '@/entities/qa';

import { CallEventsLog } from './CallEventsLog';

type CallDetailsCardProps = {
  call: CallWithDetails;
  voices?: any[];
  showManualQAFirst?: boolean;
  isLinkLead?: boolean;
}

function CallDetailsCard(
  {
    call,
    voices,
    showManualQAFirst = false,
    isLinkLead = true,
  }: CallDetailsCardProps ) {
  const navigate = useNavigate();
  const metaData = call.meta as CallWithDetails["meta"];
  const callAnalysis = call.call_analysis as CallWithDetails["call_analysis"];

  const campaignLanguage = metaData.campaignLanguage || 'en';
  const voice = voices?.find((v) => v.alt_key === metaData.voiceId);
  const voiceDisplay = voice ? `${voice.name} (${metaData.voiceId})` : metaData.voiceId || '-';
  const agentId = metaData.agentId;
  const postCallAnalysis = (callAnalysis?.auto_qa && typeof callAnalysis.auto_qa === 'object') ? callAnalysis.auto_qa as Record<string, unknown> : undefined;

  // Check if this is a dialer call
  const isDialerCall = metaData.is_dialer_call

  // Fetch signed recording URL for livekit bucket recordings
  const needsSigning = !!call.recording_link && call.recording_link.includes('livekitbucketbucket')
  const { data: recordingData } = useRecordingUrl(call.id, needsSigning)
  const recordingSrc = needsSigning ? recordingData?.url ?? undefined : (call.recording_link || undefined)

  const { showTranslate, isTranslating, handleTranslate, translation } = useTranslator(call, campaignLanguage);

  // Audio player ref for transcript timestamp seeking
  const audioRef = useRef<HTMLAudioElement>(null);
  const handleSeekAudio = (seconds: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = seconds;
      audioRef.current.play();
    }
  };

  // Manual QA section (only show for non-dialer calls)
  const manualQASection = !isDialerCall ? (<CallQASection call={call} />) : null;

  return (
    <Card className="h-full overflow-auto">
      <CardHeader className="border-b bg-gray-50 pb-4">
        <div className="space-y-2">
          <CardTitle className="text-heading">Call with {formatPhoneNumber(call.phone)}</CardTitle>
          <div className="grid grid-cols-2 gap-2 text-detail text-gray-600 items-center gap-x-4">
            <span><b>Campaign:</b> {metaData?.campaignName || '-'}</span>
            <span><b>Voice:</b> {voiceDisplay}</span>
            <span className="flex items-center gap-1">
              <b>Agent:</b>
              {
                agentId ? (
                  <>
                    {metaData?.agentName || `...${agentId.slice(-5)}`}
                    <Copy
                      className="h-3 w-3 cursor-pointer hover:text-accent-foreground"
                      onClick={() => {
                        navigator.clipboard.writeText(agentId)
                        toast.success('Agent ID copied to clipboard')
                      }}
                    />
                  </>
                ) : '-'
              }
            </span>
            <span><b>A/B test tag:</b> {metaData.abTestId || '-'}</span>
            <span><b>List:</b> {metaData?.listName || '-'}</span>
            {isLinkLead && (
              <span
                className="flex items-center gap-1 cursor-pointer hover:text-accent-foreground"
                onClick={() => {
                  navigate('/lead_search', {
                    state: {phone_number: call.phone},
                  })
                }}
              >
                Go to lead <ExternalLink className="h-3 w-3"/>
              </span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4 pb-4">
        <div className="grid grid-cols-1 gap-4">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <div className="flex items-start">
              <Timer className="h-4 w-4 text-gray-500 mr-2 mt-0.5"/>
              <div>
                <span className="text-gray-700 text-detail block">Call Start Time</span>
                <span className="text-body">{formatTimestampYear(call.callStartTime ?? 0)}</span>
              </div>
            </div>

            <div className="flex items-start">
              <Clock className="h-4 w-4 text-gray-500 mr-2 mt-0.5"/>
              <div>
                <span className="text-gray-700 text-detail block">Duration</span>
                <span className="text-body">{formatDuration(call.duration)}</span>
              </div>
            </div>

            <div className="flex items-start">
              <Save className="h-4 w-4 text-gray-500 mr-2 mt-0.5"/>
              <div>
                <span className="text-gray-700 text-detail block">Disposition</span>
                <DispositionBadge
                  disposition={call.disposition || '-'}
                  isEditable={false}
                />
              </div>
            </div>

            <div className="flex items-start">
              <AlertCircle className="h-4 w-4 text-gray-500 mr-2 mt-0.5"/>
              <div>
                <span className="text-gray-700 text-detail block">Call end reason</span>
                <span className="text-body">{formatDisconnectionReasonCallCard(call.callEndReason)}</span>
              </div>
            </div>
          </div>

          <Separator className="my-2"/>

          {/* MANUAL QA - Show first if flag is enabled */}
          {showManualQAFirst && manualQASection}

          {/* Only show for non-dialer calls */}
          {
            !isDialerCall && (
              <>
                {/* RECORDING & TRANSCRIPT */}
                <div className="space-y-3">
                  <div className="space-y-3">
                    <div className="p-3 bg-gray-50 rounded-md">
                      <audio
                        ref={audioRef}
                        key={call.id}
                        controls
                        className="w-full"
                        src={recordingSrc}
                      >
                        Your browser does not support the audio element.
                      </audio>
                    </div>

                    <div className="p-3 bg-gray-50 rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-body">Transcript</h4>
                        {showTranslate && (
                          <Button size="sm" disabled={isTranslating} onClick={handleTranslate}>
                            {isTranslating ? 'Translating…' : 'Translate to English'}
                          </Button>
                        )}
                      </div>

                      {call.transcript ? (
                        <TranscriptDisplay transcript={call.transcript} onSeek={handleSeekAudio}/>
                      ) : (
                        <div className="text-detail text-gray-500">No transcript available</div>
                      )}

                      {translation && (
                        <div className="mt-3 p-2 border rounded bg-white">
                          <div className="text-detail font-semibold mb-1">Translation</div>
                          <div className="whitespace-pre-wrap text-detail">{translation}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <Separator className="my-2"/>

                {/* AUTO QA */}
                {postCallAnalysis && (
                  <>
                    <div className="space-y-3">
                      <h3 className="font-medium text-heading">Automated QA</h3>

                      <div className="p-3 bg-gray-50 rounded-md">
                        <div className="space-y-2 text-body">
                          {Object.entries(postCallAnalysis).map(([key, value]) => (
                            <div key={key} className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{key}: </span>
                              {
                                typeof value === 'boolean' ? (
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-detail font-medium ${
                                    value ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                                    }`}>
                                    {value ? '✓ Yes' : '✗ No'}
                                  </span>
                                ) : (<span className="text-gray-900">{String(value)}</span>)
                              }
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <Separator className="my-2"/>
                  </>
                )}

              </>
            )
          }

          {/* MANUAL QA - Show last if flag is disabled (default) */}
          {!showManualQAFirst && manualQASection}

          {/* LIVEKIT EVENT LOGS - below QA */}
          {!isDialerCall && <CallEventsLog callId={call.id}/>}
        </div>
      </CardContent>
    </Card>
  )
}

type CallDetailsProps = {
  callId: string | null;
  showManualQAFirst?: boolean
  isLinkLead?: boolean
}

export function CallDetails(
  {
    callId,
    showManualQAFirst = false,
    isLinkLead = true,
  }: CallDetailsProps) {
  // Fetch selected call details
  const { isSuccess, isLoading, isError, data, error } = useCall(callId);

  // Fetch voices data
  const { data: voices } = useVoicesList();

  if (!isSuccess) {
    return (
      <Card className="h-full flex items-center justify-center max-h-[200px]">
        {!callId && (<CardContent className="pt-6 text-center text-gray-500">Select a call to view details</CardContent>)}
        {isLoading && (<CardContent className="pt-6 text-center text-gray-500">Loading call details...</CardContent>)}
        {isError && (<CardContent className="pt-6 text-center text-destructive">Error: {error.message}</CardContent>)}
      </Card>
    );
  }

  return (
    <CallDetailsCard
      call={data}
      voices={voices}
      showManualQAFirst={showManualQAFirst}
      isLinkLead={isLinkLead}
    />
  );
}